// src/components/FetchProgressBar.tsx
/**
 * Global fetch-progress bar — one slim bar pinned to the top of the viewport.
 *
 * Renders the OLDEST active job from fetchProgressStore (useActiveProgress):
 *  - Determinate mode (job.pageKnown): real fill pagesDone/estPages with
 *    page + row counts — honest progress for paginated Apaleo fetches.
 *  - Indeterminate mode: animated CSS sweep + elapsed seconds. The sweep is
 *    a pure-CSS transform animation (GPU-composited, no JS per-frame work —
 *    AGENTS.md §2.5 Layer 2 long-task budget safe).
 *  - Patience message after PATIENCE_MS ("data is large — still working")
 *    — the core fix for users believing the search crashed (2026-10-08).
 *
 * Mount ONCE (src/App.tsx, next to UploadProgressOverlay). i18n in de + en.
 * The elapsed timer lives ONLY in this component: a 1-second interval that
 * re-renders this component alone — table rows never re-render from
 * progress updates (store selector isolates the subscription).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useFetchProgress, selectActiveJob } from '../stores/fetchProgressStore';

/** Seconds before the "data is large" patience message appears. */
const PATIENCE_MS = 5_000;

export default function FetchProgressBar() {
  const job = useFetchProgress(selectActiveJob);
  const { t } = useTranslation();
  // Elapsed seconds — ticks only this component (isolated re-render).
  const [nowTs, setNowTs] = useState(() => Date.now());

  const active = !!job;
  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(timer); // teardown (AGENTS §2 realtime/timer rule)
  }, [active]);

  if (!job) return null;

  const elapsedS = Math.max(0, Math.floor((nowTs - job.startedAt) / 1000));
  const patient = nowTs - job.startedAt > PATIENCE_MS;
  const label = t(job.labelKey, { defaultValue: t('fetchProgress.generic'), ...job.labelOpts });

  const pct = job.pageKnown && job.estPages > 0
    ? Math.min(100, Math.round((job.pagesDone / job.estPages) * 100))
    : null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-999998 pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <div className={`h-1 w-full bg-border overflow-hidden ${patient ? 'animate-pulse' : ''}`}>
        {pct !== null ? (
          <div
            className="h-full bg-accent transition-all duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        ) : (
          <div className="h-full w-full fetch-progress-sweep" />
        )}
      </div>
      <div className="flex justify-center mt-1">
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface border border-border shadow-xs text-[9px] font-black uppercase tracking-widest text-muted">
          <Loader2 size={10} className="animate-spin text-accent" />
          <span>{label}</span>
          {job.pageKnown && (
            <span className="tabular-nums">
              {t('fetchProgress.pageOf', { page: job.pagesDone, total: job.estPages })}
            </span>
          )}
          {job.rowsDone > 0 && (
            <span className="tabular-nums">
              {t('fetchProgress.rows', { count: job.rowsDone })}
            </span>
          )}
          <span className="tabular-nums">{t('fetchProgress.elapsed', { seconds: elapsedS })}</span>
          {patient && <span>{t('fetchProgress.patience')}</span>}
        </div>
      </div>
    </div>
  );
}
